import { Avatar, InitialsAvatar } from '@/components/ui/Avatar';
import { hasFeature } from '@/features/feature-flags';
import { FEATURES } from '@/features/feature-flags/feature-flags.constants';
import {
  Mention,
  mentionTrailingSpace,
  removeMention,
  type MentionItem,
  type MentionSource,
} from '@/features/mention';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { queryClient } from '@/query/client';

import { withoutRequestOnly } from '../filter-people';
import { peopleWikiQueryOptions } from '../people-wiki.query';
import { type WikiPerson } from '../people-wiki.types';

import type { Editor } from '@tiptap/core';

const PERSON_KIND = 'person';

const MENU_PEOPLE_LIMIT = 5;

export const personToMention = (person: WikiPerson): MentionItem => ({
  id: person.id,
  kind: PERSON_KIND,
  name: person.name,
  iconUrl: person.avatarUrl,
  subtitle:
    [person.primaryEmail, ...person.otherEmails].filter(Boolean).join(', ') ||
    undefined,
  isOnLittlebird:
    person.isTeammate ||
    person.littlebirdAccounts.some(
      ({ connection }) => connection === 'connected'
    ),
});

export const insertPersonMention = (editor: Editor, person: WikiPerson) =>
  editor
    .chain()
    .insertContentAt(editor.state.selection.from, [
      { type: Mention.name, attrs: personToMention(person) },
      mentionTrailingSpace,
    ])
    .run();

export const removePersonMention = (editor: Editor, personId: string) =>
  removeMention(editor, { kind: PERSON_KIND, id: personId });

export const renderPersonIcon = (
  mention: MentionItem,
  surface: 'chip' | 'menu'
) => (
  <Avatar
    avatarUrl={mention.iconUrl}
    alt={mention.name}
    containerClassName={cn(
      'flex shrink-0',
      surface === 'chip' ? 'size-4' : 'size-5'
    )}
    fallback={
      <InitialsAvatar
        name={mention.name}
        seed={mention.id}
        className={cn(
          'text-[0.5rem]',
          surface === 'chip' ? 'size-4' : 'size-5'
        )}
      />
    }
  />
);

export const mentionedPersonIds = (
  mentions: readonly MentionItem[]
): string[] =>
  mentions.flatMap(({ kind, id }) => (kind === PERSON_KIND ? [id] : []));

const listWikiPeople = async (): Promise<WikiPerson[]> => {
  if (!hasFeature(FEATURES.PEOPLE_WIKI)) return [];
  try {
    return await queryClient.fetchQuery(peopleWikiQueryOptions());
  } catch (error) {
    logError(error, 'Person mentions hidden: people wiki unavailable');
    return [];
  }
};

export const createPersonSource = (): MentionSource => ({
  kind: PERSON_KIND,
  title: 'People',
  maxItems: MENU_PEOPLE_LIMIT,
  listItems: async () =>
    withoutRequestOnly(await listWikiPeople()).map(personToMention),
  renderIcon: renderPersonIcon,
});
